import React from 'react';
import { motion } from 'framer-motion';
import { Swords } from 'lucide-react';
import { wrappedTranslations } from '../../../translations/wrapped';

interface RivalsCardProps {
    rivalName: string;
    wins: number;
    losses: number;
    totalMatches: number;
    lang: 'en' | 'fr' | 'de' | 'it';
}

const RivalsCard: React.FC<RivalsCardProps> = ({ rivalName, wins, losses, totalMatches, lang }) => {
    const t = wrappedTranslations[lang];
    return (
        <div className="h-full w-full flex flex-col items-center justify-center text-center p-6">
            <div className="max-w-4xl w-full flex flex-col items-center">
                <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring" }}
                >
                    <Swords className="w-20 h-20 md:w-32 md:h-32 mb-8 mx-auto text-[#1C7AD6]" />
                </motion.div>

                <motion.h2
                    className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 text-gray-900 tracking-tight whitespace-nowrap px-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                >
                    {t.yourNemesis}
                </motion.h2>

                <motion.p
                    className="text-base md:text-lg text-gray-600 mb-8 md:mb-12 font-light px-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                >
                    {t.opponentFacedMost}
                </motion.p>

                <motion.div
                    className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8 shadow-lg w-full max-w-3xl overflow-hidden"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.6, type: "spring" }}
                >
                    <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-6 md:mb-10 tracking-tight break-words leading-tight px-2">{rivalName}</div>

                    <div className="flex justify-center items-center gap-4 md:gap-16 mb-6 md:mb-10">
                        <div className="text-center transform transition-transform hover:scale-110">
                            <div className="text-5xl md:text-7xl font-bold text-emerald-600 mb-1 md:mb-2 break-words">{wins}</div>
                            <div className="text-xs md:text-sm text-gray-600 uppercase tracking-wider font-bold">{t.wins}</div>
                        </div>
                        <div className="text-xl md:text-3xl text-gray-400 font-thin shrink-0">{t.vs}</div>
                        <div className="text-center transform transition-transform hover:scale-110">
                            <div className="text-5xl md:text-7xl font-bold text-red-600 mb-1 md:mb-2 break-words">{losses}</div>
                            <div className="text-xs md:text-sm text-gray-600 uppercase tracking-wider font-bold">{t.losses}</div>
                        </div>
                    </div>

                    <div className="bg-gray-100 rounded-2xl p-3 md:p-6 text-base md:text-lg text-gray-700 border border-gray-200 inline-block">
                        <span className="font-bold text-gray-900">{totalMatches}</span> {t.matchesTotal}
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default RivalsCard;
